import { z } from "zod";
import { zRegisterAppCard } from "./appCards.js";

/**
 * CT-RM-22: developer sandboxes, the published rate-limit contract, the CLI's
 * app manifest, and the webhook "listen" replay feed.
 */

// ---------------------------------------------------------------------------
// sandboxes
// ---------------------------------------------------------------------------

/** a production org may have at most this many live sandboxes */
export const SANDBOX_LIMIT = 2;

/** outbound channels that never leave CauseTide from a sandbox org; sends are recorded as "simulated" */
export const SANDBOX_BLOCKED_CHANNELS = ["email", "sms", "payments", "external_webhooks", "quickbooks", "connected_mailbox"] as const;
export type SandboxBlockedChannel = (typeof SANDBOX_BLOCKED_CHANNELS)[number];

export const zCreateSandbox = z.object({ name: z.string().trim().min(1).max(60) }).strict();
export type CreateSandbox = z.infer<typeof zCreateSandbox>;

export interface SandboxSummary {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
  /** last time the sandbox's data was (re)seeded */
  seededAt: string | null;
  counts: { constituents: number; gifts: number };
}

export interface SandboxList {
  limit: number;
  items: SandboxSummary[];
}

/** the status every simulated send is recorded with (outbound email, SMS, webhook delivery) */
export const SIMULATED_STATUS = "simulated" as const;

// ---------------------------------------------------------------------------
// rate limits (the published contract; mirrored in OpenAPI and /developers/api)
// ---------------------------------------------------------------------------

/**
 * Headers on every response to an API-token or OAuth-token request (success or
 * error, once the token is recognised). `RateLimit-*` follow the IETF
 * ratelimit-headers draft (Reset = seconds until the window resets); the
 * `X-RateLimit-*` forms are kept for existing clients (X-RateLimit-Reset is a
 * unix timestamp in seconds).
 */
export const RATE_LIMIT_HEADERS = {
  limit: "RateLimit-Limit",
  remaining: "RateLimit-Remaining",
  reset: "RateLimit-Reset",
  policy: "RateLimit-Policy",
  retryAfter: "Retry-After",
  legacyLimit: "X-RateLimit-Limit",
  legacyRemaining: "X-RateLimit-Remaining",
  legacyReset: "X-RateLimit-Reset",
} as const;

export type RateLimitScope = "api_token" | "oauth_app" | "public";

/** body of every 429 the API returns */
export const zRateLimitError = z.object({
  statusCode: z.literal(429),
  error: z.literal("rate_limited"),
  message: z.string(),
  /** seconds to wait; equals the Retry-After header */
  retryAfter: z.number().int().positive(),
  limit: z.number().int().positive(),
  windowSeconds: z.number().positive(),
  scope: z.enum(["api_token", "oauth_app", "public"]),
});
export type RateLimitError = z.infer<typeof zRateLimitError>;

export function rateLimitErrorBody(input: { limit: number; windowSeconds: number; retryAfter: number; scope: RateLimitScope }): RateLimitError {
  const subject = input.scope === "api_token" ? "this API token" : input.scope === "oauth_app" ? "this OAuth app and organization" : "this client";
  return {
    statusCode: 429,
    error: "rate_limited",
    message: `Rate limit of ${input.limit} requests per ${input.windowSeconds}s exceeded for ${subject}. Retry after ${input.retryAfter}s.`,
    retryAfter: input.retryAfter,
    limit: input.limit,
    windowSeconds: input.windowSeconds,
    scope: input.scope,
  };
}

export interface RateLimitPolicy {
  scope: RateLimitScope;
  /** requests per window; 0 = disabled */
  limit: number;
  windowSeconds: number;
  keyedBy: string;
}

/** GET /rate-limits — the live limits, published on /developers/api */
export interface RateLimitPolicies {
  policies: RateLimitPolicy[];
  headers: typeof RATE_LIMIT_HEADERS;
}

// ---------------------------------------------------------------------------
// whoami (CLI)
// ---------------------------------------------------------------------------

export interface WhoAmI {
  user: { id: string; name: string; email: string };
  organization: { id: string; name: string; isSandbox: boolean };
  credential: { kind: "api_token" | "oauth_app" | "session"; name: string | null };
  scopes: string[];
}

// ---------------------------------------------------------------------------
// webhook replay feed (CLI `webhooks listen`)
// ---------------------------------------------------------------------------

export const zDeliveryFeedQuery = z.object({
  /** ISO time; only deliveries created strictly after it */
  since: z.string().datetime({ offset: true }).optional(),
  subscriptionId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type DeliveryFeedQuery = z.infer<typeof zDeliveryFeedQuery>;

export interface DeliveryFeedItem {
  id: string;
  subscriptionId: string;
  event: string;
  status: string;
  createdAt: string;
}

/** a delivery re-signed now with the subscription's secret (which is never returned) */
export interface SignedDelivery {
  deliveryId: string;
  event: string;
  /** exact body to send; the signatures cover these bytes */
  body: string;
  headers: Record<string, string>;
}

// ---------------------------------------------------------------------------
// app manifest (CLI `app manifest init|validate|push`)
// ---------------------------------------------------------------------------

export const APP_MANIFEST_FILE = "causetide.app.json";

export const zAppManifest = z
  .object({
    $schema: z.string().optional(),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(500).optional(),
    cards: z
      .array(zRegisterAppCard)
      .min(1)
      .max(20)
      .refine((cards) => new Set(cards.map((c) => c.key)).size === cards.length, "card keys must be unique"),
  })
  .strict();
export type AppManifest = z.infer<typeof zAppManifest>;
